import { IPrice, IVipPrice, ListProduct, WishProduct } from '@/api/types/public';
import { useStore } from '@/providers/app-store-provider';
import { EProductImageType } from '@/utils/enums/goods';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, genPrice } from '@/utils/price';
import { Box } from '@mui/material';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { ResponsiveContext } from '../Responsive/ResponsiveProvider';
import ProductCardMedia from './ProductCardMedia';
import RedeemButton from './RedeemButton';
import SoldOutBlock from './SoldOutBlock';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';

const ClientOnlyPrice = dynamic(() => import('./Price'), { ssr: false });

export type Product = (ListProduct | WishProduct) & {
  stock?: number;
  tryon_image_url?: string;
  model_url?: string;
  correlation?: Array<
    ListProduct & {
      product_frame_color_main_url: string;
    }
  >;
  point?: number;
  point_price_info?: IPrice[];
};

const ProductRedeemCard = ({
  product,
  className,
  imageRule = [EProductImageType.PRODUCT, EProductImageType.MATERIAL, EProductImageType.CELEBRITY],
  imageHoverRule = [
    EProductImageType.MATERIAL,
    EProductImageType.CELEBRITY,
    EProductImageType.PRODUCT,
  ],
  index,
  handelClick = () => {},
  onInView = () => {},
  onValidExposure = () => {},
  isPointsMall = false,
  sendTrack = () => {},
}: {
  product: Product;
  className?: string;
  imageRule?: EProductImageType[];
  imageHoverRule?: EProductImageType[];
  index?: number;
  handelClick?: (index: number, product: Product) => void;
  onInView?: (index: number, product: Product) => void;
  onValidExposure?: (index: number, product: Product) => void;
  isPointsMall?: boolean; // 是否是积分商城商品
  sendTrack?: (index: number, product: Product, sdEventName: string) => void;
}) => {
  const [currentProduct, setCurrentProduct] = useState<Product>(product);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const { deviceType } = useContext(ResponsiveContext);
  const currency = useStore((state) => state.currency);
  const token = useStore((state) => state.token);
  const router = useRouter();
  const t = useTranslations('product');
  const [productCardRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });
  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExposure?.(index, product),
  });
  const mergedRef = useMergedRefs(productCardRef, validExposureRef);
  useEffect(() => {
    if (inView) {
      onInView?.(index, product);
    }
  }, [inView]);

  useEffect(() => {
    setCurrentProduct(product);
  }, [product]);

  let href = '';
  if (deviceType === EPlatform.WEB) {
    href = '#';
  } else {
    // 构建基础路径
    const basePath = '/account';
    const detailType = isPointsMall ? 'points-mall-detail' : 'redeemable-detail';
    href = `${basePath}/${detailType}/${currentProduct.shop_sku}`;
  }

  useEffect(() => {
    const shopSku = sessionStorage.getItem('zl-un-login-points-mall-shop-sku-click');
    if (token && shopSku === currentProduct.shop_sku) {
      sessionStorage.removeItem('zl-un-login-points-mall-shop-sku-click');
      if (deviceType === EPlatform.WEB) {
        handelClick(index, currentProduct);
      } else {
        router.push(href);
      }
    }
  }, [token]);
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // 积分商城场景下的埋点
    const sendPointsMallTrack = () => {
      if (isPointsMall) {
        sendTrack(index, currentProduct as any, 'PointsMallProductMkt_Click');
      }
    };

    // 未登录处理
    if (!token) {
      e.preventDefault();
      setShowLoginRegisterDialog(true);
      sendPointsMallTrack();
      sessionStorage.setItem('zl-un-login-points-mall-shop-sku-click', currentProduct.shop_sku);
      return;
    }

    // Web端处理
    if (deviceType === EPlatform.WEB) {
      e.preventDefault();
      handelClick(index, currentProduct);
      return;
    }

    // WAP端处理 - 只在积分商城场景下发送埋点
    if (deviceType === EPlatform.WAP) {
      sendPointsMallTrack();
    }
  };
  return (
    <div
      className={`${className} relative font-br cursor-pointer w-full overflow-hidden flex flex-col h-full`}
      ref={mergedRef}
    >
      <Link
        prefetch={false}
        href={href}
        onClick={(e) => {
          e.preventDefault();
          handleClick(e);
          if (href.includes('/account')) {
            router.push(href);
          }
        }}
        className="w-full flex flex-col h-full justify-between no-underline text-inherit"
      >
        <Box sx={{ width: '100%', flex: '0 0 auto' }}>
          <ProductCardMedia
            product={currentProduct}
            deviceType={deviceType}
            showWishNum={false}
            showWish={false}
            showTag={false}
            isAcc={currentProduct.is_glass !== 1}
            imageRule={imageRule}
            imageHoverRule={imageHoverRule}
            SoldOutBlock={<SoldOutBlock product={currentProduct} />}
            TryOnButton={null}
            isSquare
          />
        </Box>
        <Box
          className="ProductInfo"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            pt: deviceType === EPlatform.WEB ? 2 : 1,
            pb: 0,
            px: 1.5,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: deviceType === EPlatform.WEB ? 'row' : 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <span className={`${deviceType === EPlatform.WEB ? 'text-16' : 'text-12 mb-6'}`}>
              {currentProduct.short_name}
            </span>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                ...(deviceType === EPlatform.WEB && {
                  flex: 1,
                  ml: 'auto',
                  whiteSpace: 'nowrap',
                  justifyContent: 'flex-end',
                }),
              }}
            >
              {isPointsMall ? (
                <>
                  <div
                    className={`${deviceType === EPlatform.WEB ? 'text-16' : 'text-14'} text-warning font-bb`}
                  >
                    {currentProduct.point}{' '}
                    {currentProduct.point > 1 ? t('redeem.points_plural') : t('redeem.points')}
                  </div>
                  {Number(genPrice(currentProduct.point_price_info, currency.name).sell_price) >
                    0 && (
                    <span
                      className={`${deviceType === EPlatform.WEB ? 'text-16' : 'text-14'} font-bb notranslate`}
                    >
                      &nbsp;+&nbsp;
                      {formatPriceNumberWithSymbol(
                        genPrice(currentProduct.point_price_info, currency.name).sell_price,
                        currency,
                      )}
                    </span>
                  )}
                </>
              ) : (
                <ClientOnlyPrice
                  priceObject={currentProduct as IVipPrice}
                  renderPrice={(price, sellPrice) => (
                    <span
                      className={`${deviceType === EPlatform.WEB ? 'text-16' : 'text-14'} font-bb`}
                    >
                      {price || sellPrice}
                    </span>
                  )}
                />
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mt: deviceType === EPlatform.WEB ? 3 : 2,
            px: deviceType === EPlatform.WEB ? 1.5 : 1.75,
            height: deviceType === EPlatform.WEB ? '40px' : '28px',
          }}
        >
          <RedeemButton
            deviceType={deviceType}
            disabled={currentProduct.stock <= 0 || currentProduct.status === 2}
          >
            {isPointsMall ? t('redeem.redeemButton') : t('redeem.redeemedItem')}
          </RedeemButton>
        </Box>
      </Link>
    </div>
  );
};

export default ProductRedeemCard;
